import { ethers } from 'ethers';

// 扩展Window接口以包含ethereum
declare global {
  interface Window {
    ethereum?: any;
  }
}

export interface WalletInfo {
  address: string;
  chainId: string;
  networkName: string;
  connectedAt: number;
  provider: string; // 'metamask' | 'coinbase' | 'trust' | 'other'
}

export interface WalletSession {
  isConnected: boolean;
  walletInfo: WalletInfo | null;
  sessionId: string;
  lastActivity: number;
}

class WalletManager {
  private static instance: WalletManager;
  private session: WalletSession | null = null;
  private sessionId: string = '';
  private readonly SESSION_KEY = 'wallet-session';
  private readonly SESSION_TIMEOUT = 24 * 60 * 60 * 1000; // 24小时

  private constructor() {
    this.sessionId = this.generateSessionId();
    this.loadSession();
    this.setupEventListeners();
  }

  public static getInstance(): WalletManager {
    if (!WalletManager.instance) {
      WalletManager.instance = new WalletManager();
    }
    return WalletManager.instance;
  }

  // 生成会话ID
  private generateSessionId(): string {
    return `wallet_session_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
  }

  // 加载会话
  private loadSession(): void {
    try {
      const stored = sessionStorage.getItem(this.SESSION_KEY);
      if (stored) {
        const session: WalletSession = JSON.parse(stored);
        
        // 检查会话是否过期
        if (Date.now() - session.lastActivity < this.SESSION_TIMEOUT) {
          this.session = session;
          console.log('Wallet session restored:', session);
        } else {
          console.log('Wallet session expired, clearing...');
          this.clearSession();
        }
      }
    } catch (error) {
      console.error('Error loading wallet session:', error);
      this.clearSession();
    }
  }

  // 保存会话
  private saveSession(): void {
    if (this.session) {
      this.session.lastActivity = Date.now();
      sessionStorage.setItem(this.SESSION_KEY, JSON.stringify(this.session));
    }
  }

  // 清除会话
  private clearSession(): void {
    this.session = null;
    sessionStorage.removeItem(this.SESSION_KEY);
  }

  // 设置事件监听器
  private setupEventListeners(): void {
    if (typeof window !== 'undefined' && window.ethereum) {
      // 监听账户变化
      window.ethereum.on('accountsChanged', (accounts: string[]) => {
        console.log('Accounts changed:', accounts);
        if (accounts.length === 0) {
          this.disconnect();
          this.notifyStatusChange();
        } else {
          this.updateWalletInfo();
          this.notifyStatusChange();
        }
      });

      // 监听网络变化
      window.ethereum.on('chainChanged', (chainId: string) => {
        console.log('Chain changed:', chainId);
        this.updateWalletInfo();
        this.notifyStatusChange();
      });

      // 监听连接状态
      window.ethereum.on('connect', (connectInfo: any) => {
        console.log('Wallet connected:', connectInfo);
        this.updateWalletInfo();
        this.notifyStatusChange();
      });

      // 监听断开连接
      window.ethereum.on('disconnect', (error: any) => {
        console.log('Wallet disconnected:', error);
        this.disconnect();
        this.notifyStatusChange();
      });
    }
  }

  // 通知状态变化
  private notifyStatusChange(): void {
    // 触发自定义事件，通知其他组件状态变化
    const event = new CustomEvent('walletStatusChanged', {
      detail: {
        isConnected: this.session?.isConnected || false,
        walletInfo: this.session?.walletInfo || null
      }
    });
    window.dispatchEvent(event);
  }

  // 检查是否已连接 - 实时验证钱包状态
  public async isConnected(): Promise<boolean> {
    if (!this.session?.isConnected || !this.session?.walletInfo) {
      return false;
    }
    
    try {
      // 按钱包类型分别验证
      const providerType = this.session.walletInfo.provider;
      if (providerType === 'phantom') {
        const phantom = (window as any)?.phantom?.solana;
        if (!phantom) { this.disconnect(); return false; }
        const isPhantomConnected = phantom.isConnected || false;
        if (!isPhantomConnected) { this.disconnect(); return false; }
        const pk = phantom.publicKey?.toString?.();
        if (!pk || pk !== this.session.walletInfo.address) { this.disconnect(); return false; }
        return true;
      } else {
        if (!window.ethereum) { this.disconnect(); return false; }
        const accounts = await window.ethereum.request({ method: 'eth_accounts' });
        if (!accounts || accounts.length === 0) { this.disconnect(); return false; }
        const currentAccount = accounts[0];
        if (this.session.walletInfo.address !== currentAccount) { this.disconnect(); return false; }
        return true;
      }
    } catch (error) {
      console.error('Wallet connection verification failed:', error);
      this.disconnect();
      return false;
    }
  }

  // 获取钱包信息
  public getWalletInfo(): WalletInfo | null {
    return this.session?.walletInfo || null;
  }

  // 获取会话信息
  public getSession(): WalletSession | null {
    return this.session;
  }

  // 连接钱包
  public async connectWallet(): Promise<{ success: boolean; walletInfo?: WalletInfo; error?: string }> {
    // 如果检测到Phantom并且未注入以太坊，走Solana流程
    if (!(window as any).ethereum && (window as any)?.phantom?.solana) {
      return this.connectSolanaWallet();
    }
    try {
      if (!window.ethereum) {
        return { success: false, error: '请安装MetaMask或其他Web3钱包' };
      }

      console.log('开始连接钱包...');
      
      // 首先检查是否已经有账户连接
      let accounts: string[] = [];
      try {
        const existingAccounts = await window.ethereum.request({ method: 'eth_accounts' });
        if (existingAccounts && existingAccounts.length > 0) {
          console.log('发现已连接的账户:', existingAccounts);
          accounts = existingAccounts;
        } else {
          console.log('没有已连接的账户，请求新连接...');
          // 请求账户访问，缩短超时时间到15秒
          console.log('正在请求钱包授权...');
          accounts = await Promise.race([
            window.ethereum.request({ method: 'eth_requestAccounts' }),
            new Promise((_, reject) => 
              setTimeout(() => reject(new Error('连接超时，请重试')), 15000)
            )
          ]) as string[];
        }
      } catch (error: any) {
        console.log('检查现有账户时出错，尝试请求新连接:', error);
        // 如果是用户拒绝，不显示超时错误
        if (error.code === 4001 || error.message?.includes('User rejected')) {
          return { success: false, error: '用户拒绝了连接请求' };
        }
        // 请求账户访问
        console.log('正在请求钱包授权...');
        try {
          accounts = await Promise.race([
            window.ethereum.request({ method: 'eth_requestAccounts' }),
            new Promise((_, reject) => 
              setTimeout(() => reject(new Error('连接超时，请重试')), 15000)
            )
          ]) as string[];
        } catch (requestError: any) {
          if (requestError.code === 4001 || requestError.message?.includes('User rejected')) {
            return { success: false, error: '用户拒绝了连接请求' };
          }
          throw requestError;
        }
      }
      
      console.log('获取到账户:', accounts);
      
      if (!accounts || accounts.length === 0) {
        return { success: false, error: '用户拒绝了连接请求' };
      }

      // 获取网络信息（增加健壮性，避免部分钱包抛出意外错误）
      let chainId = '0x0';
      let networkName = 'Unknown';
      try {
        // 方式一：通过以太请求
        const hexId = await window.ethereum.request({ method: 'eth_chainId' });
        chainId = typeof hexId === 'string' ? parseInt(hexId, 16).toString() : String(hexId);
      } catch (_) { /* 忽略 */ }
      try {
        // 方式二：通过 ethers BrowserProvider
        const provider = new ethers.BrowserProvider(window.ethereum);
        const network = await provider.getNetwork();
        chainId = network?.chainId ? network.chainId.toString() : chainId;
        networkName = (network as any)?.name || networkName;
      } catch (e) {
        console.warn('getNetwork failed, fallback to defaults:', e);
      }

      // 检测钱包类型
      const providerType = this.detectProviderType();

      const walletInfo: WalletInfo = {
        address: accounts[0],
        chainId: chainId,
        networkName: networkName,
        connectedAt: Date.now(),
        provider: providerType,
      };

      // 创建会话
      this.session = {
        isConnected: true,
        walletInfo: walletInfo,
        sessionId: this.sessionId,
        lastActivity: Date.now(),
      };

      this.saveSession();
      console.log('Wallet connected successfully:', walletInfo);

      // 通知状态变化
      this.notifyStatusChange();

      return { success: true, walletInfo };
    } catch (error: any) {
      console.error('Wallet connection failed:', error);
      const msg = typeof error?.message === 'string' ? error.message : String(error);
      // 统一用户可读错误
      const normalized =
        msg.includes('User rejected') || error?.code === 4001 ? '用户拒绝了连接请求' :
        msg.includes('Already processing') ? '钱包正在处理请求，请稍后重试' :
        msg.includes('timeout') ? '连接超时，请重试' :
        msg || '连接钱包失败';
      return { success: false, error: normalized };
    }
  }

  // 连接 Phantom（Solana）
  public async connectSolanaWallet(): Promise<{ success: boolean; walletInfo?: WalletInfo; error?: string }> {
    try {
      const phantom = (window as any)?.phantom?.solana;
      if (!phantom) {
        return { success: false, error: '未检测到 Phantom，请先安装或启用' };
      }
      // 请求连接
      const resp = await phantom.connect();
      const address = resp?.publicKey?.toString?.();
      if (!address) {
        return { success: false, error: '未获取到钱包地址' };
      }

      const walletInfo: WalletInfo = {
        address,
        chainId: 'solana',
        networkName: 'Solana',
        connectedAt: Date.now(),
        provider: 'phantom',
      };

      this.session = {
        isConnected: true,
        walletInfo,
        sessionId: this.sessionId,
        lastActivity: Date.now(),
      };
      this.saveSession();
      this.notifyStatusChange();
      return { success: true, walletInfo };
    } catch (error: any) {
      const msg = typeof error?.message === 'string' ? error.message : String(error);
      if (error?.code === 4001) {
        return { success: false, error: '用户拒绝了连接请求' };
      }
      return { success: false, error: msg || '连接 Phantom 失败' };
    }
  }

  // 自动连接（如果之前已连接）
  public async autoConnect(): Promise<{ success: boolean; walletInfo?: WalletInfo; error?: string }> {
    try {
      if (!this.isConnected()) {
        return { success: false, error: 'No active session' };
      }

      if (!window.ethereum) {
        return { success: false, error: 'Web3 wallet not available' };
      }

      // 检查账户是否仍然可用
      const accounts = await window.ethereum.request({ method: 'eth_accounts' });
      
      if (accounts.length === 0) {
        this.disconnect();
        return { success: false, error: 'Wallet not connected' };
      }

      // 更新钱包信息
      await this.updateWalletInfo();
      
      if (this.session?.walletInfo) {
        console.log('Wallet auto-connected:', this.session.walletInfo);
        return { success: true, walletInfo: this.session.walletInfo };
      }

      return { success: false, error: 'Failed to update wallet info' };
    } catch (error: any) {
      console.error('Auto-connect failed:', error);
      this.disconnect();
      return { success: false, error: error.message || 'Auto-connect failed' };
    }
  }

  // 更新钱包信息
  private async updateWalletInfo(): Promise<void> {
    if (!this.session?.walletInfo || !window.ethereum) return;

    try {
      const provider = new ethers.BrowserProvider(window.ethereum);
      const network = await provider.getNetwork();
      
      this.session.walletInfo.chainId = network.chainId.toString();
      this.session.walletInfo.networkName = network.name || 'Unknown';
      this.session.lastActivity = Date.now();
      
      this.saveSession();
    } catch (error) {
      console.error('Error updating wallet info:', error);
    }
  }

  // 检测钱包类型
  private detectProviderType(): string {
    if (!window.ethereum) return 'unknown';

    console.log('检测钱包类型，ethereum对象:', window.ethereum);
    console.log('钱包属性:', {
      isMetaMask: window.ethereum.isMetaMask,
      isCoinbaseWallet: window.ethereum.isCoinbaseWallet,
      isTrust: window.ethereum.isTrust,
      isBinance: window.ethereum.isBinance,
      isImToken: window.ethereum.isImToken,
      isBitgetWallet: window.ethereum.isBitgetWallet,
      isOkxWallet: window.ethereum.isOkxWallet,
      isTokenPocket: window.ethereum.isTokenPocket,
      isRainbow: window.ethereum.isRainbow,
      isBraveWallet: window.ethereum.isBraveWallet,
      isPhantom: window.ethereum.isPhantom,
    });

    // 检查钱包提供商的标识符
    const providerInfo = window.ethereum.providerInfo || window.ethereum.providers;
    console.log('Provider info:', providerInfo);

    // 通过钱包名称检测
    const walletName = window.ethereum.walletName || window.ethereum.name;
    console.log('Wallet name:', walletName);

    if (walletName) {
      const name = walletName.toLowerCase();
      if (name.includes('binance')) return 'binance';
      if (name.includes('coinbase')) return 'coinbase';
      if (name.includes('trust')) return 'trust';
      if (name.includes('imtoken')) return 'imtoken';
      if (name.includes('bitget')) return 'bitget';
      if (name.includes('okx')) return 'okx';
      if (name.includes('tokenpocket')) return 'tokenpocket';
      if (name.includes('rainbow')) return 'rainbow';
      if (name.includes('brave')) return 'brave';
      if (name.includes('phantom')) return 'phantom';
      if (name.includes('metamask')) return 'metamask';
    }

    // 通过特定属性检测
    if (window.ethereum.isMetaMask && !window.ethereum.isBraveWallet) return 'metamask';
    if (window.ethereum.isCoinbaseWallet) return 'coinbase';
    if (window.ethereum.isTrust) return 'trust';
    if (window.ethereum.isBinance) return 'binance';
    if (window.ethereum.isImToken) return 'imtoken';
    if (window.ethereum.isBitgetWallet) return 'bitget';
    if (window.ethereum.isOkxWallet) return 'okx';
    if (window.ethereum.isTokenPocket) return 'tokenpocket';
    if (window.ethereum.isRainbow) return 'rainbow';
    if (window.ethereum.isBraveWallet) return 'brave';
    if (window.ethereum.isPhantom) return 'phantom';
    
    // 通过用户代理检测
    const userAgent = navigator.userAgent.toLowerCase();
    if (userAgent.includes('binance')) return 'binance';
    if (userAgent.includes('coinbase')) return 'coinbase';
    if (userAgent.includes('trust')) return 'trust';
    if (userAgent.includes('imtoken')) return 'imtoken';
    if (userAgent.includes('bitget')) return 'bitget';
    if (userAgent.includes('okx')) return 'okx';
    if (userAgent.includes('tokenpocket')) return 'tokenpocket';
    if (userAgent.includes('rainbow')) return 'rainbow';
    if (userAgent.includes('brave')) return 'brave';
    if (userAgent.includes('phantom')) return 'phantom';
    
    // 通过域名检测（如果是iframe）
    if (typeof window !== 'undefined') {
      const hostname = window.location.hostname.toLowerCase();
      if (hostname.includes('binance')) return 'binance';
      if (hostname.includes('coinbase')) return 'coinbase';
      if (hostname.includes('trust')) return 'trust';
    }
    
    console.log('未识别的钱包类型，使用默认值');
    return 'other';
  }

  // 断开连接
  public disconnect(): void {
    console.log('Disconnecting wallet...');
    this.clearSession();
    this.notifyStatusChange();
  }

  // 获取连接状态文本
  public getConnectionStatus(): string {
    if (!this.isConnected()) {
      return '未连接';
    }

    const walletInfo = this.getWalletInfo();
    if (!walletInfo) {
      return '连接状态未知';
    }

    const timeConnected = Date.now() - walletInfo.connectedAt;
    const hoursConnected = Math.floor(timeConnected / (1000 * 60 * 60));
    
    if (hoursConnected < 1) {
      return '刚刚连接';
    } else if (hoursConnected < 24) {
      return `已连接 ${hoursConnected} 小时`;
    } else {
      const daysConnected = Math.floor(hoursConnected / 24);
      return `已连接 ${daysConnected} 天`;
    }
  }

  // 获取钱包地址的简短形式
  public getShortAddress(): string {
    const walletInfo = this.getWalletInfo();
    if (!walletInfo) return '';
    
    return `${walletInfo.address.slice(0, 6)}...${walletInfo.address.slice(-4)}`;
  }

  // 检查会话是否活跃
  public isSessionActive(): boolean {
    if (!this.session) return false;
    
    const timeSinceLastActivity = Date.now() - this.session.lastActivity;
    return timeSinceLastActivity < this.SESSION_TIMEOUT;
  }

  // 刷新会话活动时间
  public refreshActivity(): void {
    if (this.session) {
      this.session.lastActivity = Date.now();
      this.saveSession();
    }
  }

  // 获取会话统计信息
  public getSessionStats(): {
    isConnected: boolean;
    connectedTime: string;
    sessionDuration: string;
    provider: string;
    network: string;
  } {
    const isConnected = this.isConnected();
    const walletInfo = this.getWalletInfo();
    
    if (!isConnected || !walletInfo) {
      return {
        isConnected: false,
        connectedTime: '',
        sessionDuration: '',
        provider: '',
        network: '',
      };
    }

    const connectedTime = new Date(walletInfo.connectedAt).toLocaleString();
    const sessionDuration = this.getConnectionStatus();
    
    return {
      isConnected: true,
      connectedTime,
      sessionDuration,
      provider: walletInfo.provider,
      network: walletInfo.networkName,
    };
  }
}

// 导出单例实例
export const walletManager = WalletManager.getInstance();
